/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  AzureCommunicationTokenCredential,
  getIdentifierRawId,
} from "@azure/communication-common";
import {
  CallClient,
  VideoStreamRenderer,
  LocalVideoStream,
  TeamsCallAgent,
  TeamsCall,
  RemoteParticipant,
  RemoteVideoStream,
  CreateViewOptions,
} from "@azure/communication-calling";

import qs from "query-string";
import { Trans } from "react-i18next";

import { useLocation } from "react-router-dom";

import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import Title from "../../../components/Title";
import Curve from "../../../components/Animations/PageTransitions/Curve";
import BackButton from "../../../components/BackButton";

import Camera from "../../../assets/icons/Camera.svg";
import CameraOff from "../../../assets/icons/CameraOff.svg";
import Calling from "../../../assets/icons/Calling.svg";
import Microphone from "../../../assets/icons/Microphone.svg";
import MicrophoneOff from "../../../assets/icons/MicrophoneOff.svg";
import Bell from "../../../assets/icons/Bell.svg";

import { useRingTimeout } from "../../../lib/hooks/useRingTimeout";
import { useCheckinAccessNavigation } from "../../../lib/hooks/useCheckinAccessNavigation";

const Controls = styled("div")({
  marginTop: "1rem",
  width: "70%",

  "@media screen and (orientation: landscape)": {
    marginTop: "0",
    width: "37%",
  },
});

const CallControl = styled("div")<{ isEnd?: boolean }>(({ theme, isEnd }) => ({
  display: "flex",
  alignItems: "center",
  height: "7.25rem",
  padding: "1.75rem 4.5rem",
  margin: isEnd ? "0" : "5rem 0 0",
  border: "2px solid white",
  borderRadius: "1rem",
  backgroundColor: isEnd ? "#D13438" : theme.palette.primary.main,

  h3: {
    color: "white",
    marginLeft: "2.75rem",
    fontSize: "2.75rem",
  },

  ":only-of-type": isEnd
    ? {
        marginLeft: "auto",
        marginBottom: "2rem",
      }
    : {},
}));

const StreamControl = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "1.75rem 0",
  width: "17rem",

  h3: {
    marginTop: "1rem",
    color: "black",
    fontSize: "2.25rem",
    textAlign: "center",
  },
});
const StreamsContainer = styled("div")({
  width: "50%",
  height: "100%",
  display: "flex",
  flexDirection: "column",

  "@media screen and (orientation: landscape)": {
    flexDirection: "row",
    width: "50%",
  },
});

const Stream = styled("div")({
  width: "100%",
  height: "50%",
  border: "1px solid gray",
  background: " #f2f2f2",
  borderRadius: "1rem",
  overflow: "hidden",
  margin: "1.5rem 0 0 0",

  "@media screen and (orientation: landscape)": {
    width: "50%",
    height: "100%",
    margin: "0 0 0 1.5rem",
  },
});

const StreamMedia = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,

  "& > *": {
    width: "100%",
    height: "100%",
  },

  "& video": {
    width: "100%",
    height: "100%",
  },
});

const Initials = styled("div")({
  width: "10rem",
  height: "10rem",
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#D3D3D3",
  color: "black",
  fontSize: "3rem",
  fontWeight: "bold",
});

const VIEW_OPTIONS: CreateViewOptions = { scalingMode: "Crop" };

/**
 * How long the "nobody answered" message stays on screen before the hang-up
 * takes the visitor back.
 */
const NO_ANSWER_MESSAGE_MS = 4000;

const getInitials = (displayName?: string) =>
  (displayName || "Unknown")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const TeamsVideoCall = () => {
  const { search } = useLocation();

  const query = qs.parse(search);

  const callerAcsToken = query.callerAcsToken as string;
  const targetTeamsUserIds = query.targetTeamsUserIds as string;
  const targetTeamsUserId = targetTeamsUserIds?.split(",")[0];

  const type = query.type as string;

  const { handleCheckinAccessNavigation, accessSwitchModal } =
    useCheckinAccessNavigation({
      type,
    });

  const [triggerCall, setTriggerCall] = useState<boolean>(false);

  const [callClient, setCallClient] = useState<CallClient | null>(null);
  const [teamsCallAgent, setTeamsCallAgent] = useState<TeamsCallAgent | null>(
    null,
  );

  const [call, setCall] = useState<TeamsCall | null>(null);
  const callRef = useRef<TeamsCall | null>(null);

  useEffect(() => {
    callRef.current = call;
  }, [call]);

  const localVideoRef = useRef<HTMLDivElement | null>(null);
  const [localVideoStream, setLocalVideoStream] =
    useState<LocalVideoStream | null>(null);
  const [localVideoRenderer, setLocalVideoRenderer] =
    useState<VideoStreamRenderer | null>(null);

  const remoteVideoRef = useRef<HTMLDivElement | null>(null);

  const remoteVideoRendererRef = useRef<VideoStreamRenderer | null>(null);

  const handledParticipantIds = useRef(new Set<string>());

  const [isMicActive, setIsMicActive] = useState<boolean>(true);

  const [displayRemoteInitials, setDisplayRemoteInitials] =
    useState<boolean>(false);
  const [remoteParticipantInitials, setRemoteParticipantInitials] =
    useState<string>();

  const [isRinging, setIsRinging] = useState<boolean>(false);
  const [hasNoAnswer, setHasNoAnswer] = useState<boolean>(false);

  const handleRingTimeout = useCallback(() => {
    console.log("The call was never answered. Hanging up.");

    setIsRinging(false);
    setHasNoAnswer(true);

    setTimeout(async () => {
      // A call that connected during the message is left alone.
      if (callRef.current?.state !== "Ringing") {
        setHasNoAnswer(false);
        return;
      }

      try {
        await callRef.current.hangUp();
      } catch (error) {
        console.log("Error hanging up unanswered call:", error);
      }
    }, NO_ANSWER_MESSAGE_MS);
  }, []);

  useRingTimeout(isRinging, handleRingTimeout);

  const createLocalVideoStream = useCallback(async () => {
    if (!callClient) {
      console.log(
        "The local video stream was not created. The call client was not initialized.",
      );
      return null;
    }

    try {
      const deviceManager = await callClient.getDeviceManager();

      const camera = (await deviceManager.getCameras())[0];

      if (camera) {
        const stream = new LocalVideoStream(camera);

        setLocalVideoStream(stream);

        return stream;
      }

      console.log(
        "The local video stream was not created. No camera device found.",
      );
      return null;
    } catch (error) {
      console.log("Failed to create local video stream:", error);
      return null;
    }
  }, [callClient]);

  const startLocalVideo = useCallback(async () => {
    if (localVideoStream) {
      const renderer = new VideoStreamRenderer(localVideoStream);

      const view = await renderer.createView(VIEW_OPTIONS);

      setLocalVideoRenderer(renderer);

      localVideoRef.current?.replaceChildren(view.target);

      if (call) {
        await call.startVideo(localVideoStream);
      }
    }
  }, [localVideoStream, call]);

  const stopLocalVideo = useCallback(async () => {
    if (call && localVideoStream) {
      await call.stopVideo(localVideoStream);
    }

    if (localVideoRenderer) {
      localVideoRenderer.dispose();

      setLocalVideoRenderer(null);
    }
  }, [localVideoRenderer, call, localVideoStream]);

  const clearRemoteStream = useCallback(() => {
    remoteVideoRendererRef.current?.dispose();
    remoteVideoRendererRef.current = null;

    remoteVideoRef.current?.replaceChildren();

    setDisplayRemoteInitials(true);
  }, []);

  const renderRemoteStream = useCallback(async (stream: RemoteVideoStream) => {
    remoteVideoRendererRef.current?.dispose();

    const renderer = new VideoStreamRenderer(stream);

    const view = await renderer.createView(VIEW_OPTIONS);

    remoteVideoRendererRef.current = renderer;
    remoteVideoRef.current?.replaceChildren(view.target);

    setDisplayRemoteInitials(false);
  }, []);

  const handleRemoteVideoStream = useCallback(
    (stream: RemoteVideoStream) => {
      if (stream.mediaStreamType !== "Video") {
        return;
      }

      stream.on("isAvailableChanged", () => {
        if (stream.isAvailable) {
          renderRemoteStream(stream);
          return;
        }

        clearRemoteStream();
      });

      if (stream.isAvailable) {
        renderRemoteStream(stream);
        return;
      }

      setDisplayRemoteInitials(true);
    },
    [renderRemoteStream, clearRemoteStream],
  );

  const handleRemoteParticipant = useCallback(
    (participant?: RemoteParticipant) => {
      if (!participant) {
        return;
      }

      const participantId = getIdentifierRawId(participant.identifier);

      if (handledParticipantIds.current.has(participantId)) {
        return;
      }

      handledParticipantIds.current.add(participantId);

      setRemoteParticipantInitials(getInitials(participant.displayName));

      setDisplayRemoteInitials(true);

      participant.videoStreams.forEach(handleRemoteVideoStream);

      participant.on("videoStreamsUpdated", ({ added, removed }) => {
        added.forEach(handleRemoteVideoStream);

        if (removed.some((stream) => stream.mediaStreamType === "Video")) {
          clearRemoteStream();
        }
      });
    },
    [handleRemoteVideoStream, clearRemoteStream],
  );

  const startCall = useCallback(async () => {
    if (!teamsCallAgent || !targetTeamsUserId) {
      console.log(
        "Cannot start call: Target Teams User Id is missing or Teams Call Agent not initialized.",
      );
      return;
    }

    try {
      const localStream = await createLocalVideoStream();

      if (!localStream) {
        return;
      }

      const options = { videoOptions: { localVideoStreams: [localStream] } };

      const call = teamsCallAgent.startCall(
        { microsoftTeamsUserId: targetTeamsUserId.trim() },
        options,
      );

      setCall(call);

      setIsMicActive(!call.isMuted);

      const renderer = new VideoStreamRenderer(localStream);

      const view = await renderer.createView(VIEW_OPTIONS);

      setLocalVideoRenderer(renderer);

      localVideoRef.current?.appendChild(view.target);

      call.remoteParticipants.forEach(handleRemoteParticipant);

      call.on("remoteParticipantsUpdated", ({ added }) => {
        added.forEach(handleRemoteParticipant);
      });

      call.on("stateChanged", () => {
        console.log("Call state : ", call.state);

        if (call.state === "Disconnected") {
          handleCheckinAccessNavigation();
        }

        if (call.state === "Ringing") {
          setIsRinging(true);
        } else {
          setIsRinging(false);
        }
      });

      console.log("Call started.");
    } catch (error) {
      console.log("Failed to start call: ", error);
    }
  }, [
    teamsCallAgent,
    targetTeamsUserId,
    createLocalVideoStream,
    handleRemoteParticipant,
  ]);

  const hangUpCall = useCallback(async () => {
    if (!call) {
      return;
    }

    await call.hangUp();

    clearRemoteStream();

    handleCheckinAccessNavigation();
  }, [call, clearRemoteStream]);

  const toggleMicrophone = useCallback(async () => {
    if (!call) {
      return;
    }

    if (isMicActive) {
      call.mute();
    } else {
      call.unmute();
    }

    setIsMicActive((prev) => !prev);
  }, [call, isMicActive]);

  useEffect(() => {
    if (triggerCall) {
      startCall();
    }
  }, [triggerCall]);

  useEffect(() => {
    const initializeCallAgent = async () => {
      if (!callerAcsToken) {
        handleCheckinAccessNavigation();
        return;
      }

      try {
        const client = new CallClient();

        const tokenCredential = new AzureCommunicationTokenCredential(
          callerAcsToken.trim(),
        );

        const agent = await client.createTeamsCallAgent(tokenCredential);

        setCallClient(client);
        setTeamsCallAgent(agent);

        const deviceManager = await client.getDeviceManager();

        await deviceManager.askDevicePermission({ video: true, audio: true });

        console.log("Call agent initialized.");

        setTriggerCall(true);
      } catch (error) {
        console.log("Failed to initialize call agent:", error);
      }
    };

    initializeCallAgent();
  }, [callerAcsToken]);

  useEffect(() => {
    return () => {
      (async () => {
        if (callRef.current?.state !== "Disconnected") {
          await callRef.current?.hangUp();
        }

        remoteVideoRendererRef.current?.dispose();
        remoteVideoRendererRef.current = null;

        setCall(null);

        setLocalVideoStream(null);

        setLocalVideoRenderer(null);
      })();
    };
  }, []);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <BackButton to="/" />
        <Title className="mt-3 mb-4">
          <Typography variant="h1">
            <Trans i18nKey="teamsCall:title" />
          </Typography>
        </Title>
        <Controls className="d-flex justify-content-between align-items-center">
          {(!teamsCallAgent || !call) && (
            <CircularProgress
              size="8rem"
              style={{
                marginTop: "5rem",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          )}
          {!!call && (
            <>
              <div className="d-flex">
                <StreamControl
                  onClick={() => {
                    if (!localVideoRenderer) {
                      startLocalVideo();
                      return;
                    }
                    stopLocalVideo();
                  }}
                >
                  {!!localVideoRenderer ? (
                    <>
                      <Camera width="4rem" fill="black" />
                      <Typography variant="h3">
                        <Trans i18nKey="teamsCall:controls.camera" />
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CameraOff width="4rem" fill="black" />
                      <Typography variant="h3">
                        <Trans i18nKey="teamsCall:controls.cameraOff" />
                      </Typography>
                    </>
                  )}
                </StreamControl>
                <StreamControl onClick={toggleMicrophone}>
                  {isMicActive ? (
                    <>
                      <Microphone width="4rem" fill="black" />
                      <Typography variant="h3">
                        <Trans i18nKey="teamsCall:controls.mic" />
                      </Typography>
                    </>
                  ) : (
                    <>
                      <MicrophoneOff width="4rem" fill="black" />
                      <Typography variant="h3">
                        <Trans i18nKey="teamsCall:controls.micOff" />
                      </Typography>
                    </>
                  )}
                </StreamControl>
              </div>

              <CallControl onClick={hangUpCall} isEnd>
                <Calling
                  width="3.5rem"
                  fill="white"
                  style={{ transform: "rotate(135deg)" }}
                />
                <Typography variant="h3">
                  <Trans i18nKey="teamsCall:controls.endCall" />
                </Typography>
              </CallControl>
            </>
          )}
        </Controls>
        {!!call && (
          <StreamsContainer>
            <Stream className="position-relative">
              {!localVideoRenderer && (
                <div className="position-absolute h-100 w-100 d-flex justify-content-center align-items-center">
                  <Initials className="initials">DR</Initials>
                </div>
              )}
              <StreamMedia id="localVideo" ref={localVideoRef} />
            </Stream>
            <Stream className="position-relative">
              {displayRemoteInitials && !isRinging && !hasNoAnswer && (
                <div className="position-absolute h-100 w-100 d-flex justify-content-center align-items-center">
                  <Initials className="initials position-relative">
                    {remoteParticipantInitials}
                  </Initials>
                </div>
              )}
              {(isRinging || hasNoAnswer) && (
                <div className="position-absolute h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                  <Bell width="3.5rem" />
                  <Typography variant="h4" className="fs-3 mt-2">
                    <Trans
                      i18nKey={
                        hasNoAnswer
                          ? "teamsCall:controls.noAnswer"
                          : "teamsCall:controls.ringing"
                      }
                    />
                  </Typography>
                </div>
              )}
              <StreamMedia id="remoteVideo" ref={remoteVideoRef} />
            </Stream>
          </StreamsContainer>
        )}
        {accessSwitchModal}
      </div>
    </Curve>
  );
};

export default TeamsVideoCall;
